import { useState } from "react";

/**
 * **「2×2 격자」가 뭔지 모르는 사람**을 위한 도움말 — 화면 안에서 끝나게.
 *
 * ## 왜 필요한가
 * 격자로 받는 이유(생성기가 가로 스트립을 못 그린다)는 개발 문서에만 있었다.
 * 운영자는 그 문서를 안 읽고, **무엇을 올려야 하는지조차 모른다.**
 * 「파일 고르기」만 있으면 아무 그림이나 올리고 실패한다.
 *
 * 그래서 ①어떻게 생긴 것을 올리는지(그림) ②어디서 어떻게 받는지(프롬프트)
 * ③자주 틀리는 것 — 셋을 버튼 옆에 둔다. 기본은 접어 둔다(아는 사람에겐 방해다).
 */

/** 붙여넣을 프롬프트. 문서(docs/build/29 §4-4)와 같은 문장이다. */
const PROMPT = `Same child as the attached image, same art style, same colors, same clothes.
Draw a 2x2 grid of exactly 4 equal square cells showing one walk cycle, SIDE view:
  top-left: legs apart, left leg forward
  top-right: legs together, passing
  bottom-left: legs apart, right leg forward
  bottom-right: legs together, passing
IMPORTANT: the child must be the SAME SIZE and SAME HEIGHT in all four cells,
standing on the same ground line, seen from the same distance.
Plain pure white background, no shadow, no ground, no grid lines, no borders, no text.`;

/** 어떻게 생긴 것을 올려야 하는지 — 말보다 그림이 빠르다. */
function GridShape() {
  const cell = (x: number, y: number, n: string, legs: number, label: string) => (
    <g transform={`translate(${x},${y})`}>
      <rect width="86" height="86" rx="6" fill="#fffdf8" stroke="#d9ccb8" strokeWidth="1.5" />
      <g transform="translate(43,58)">
        <rect x={-9 + legs} y="6" width="7" height="20" rx="3" fill="#6b8fa8" />
        <rect x={2 - legs} y="6" width="7" height="20" rx="3" fill="#4b86b8" />
        <rect x="-14" y="-14" width="28" height="24" rx="9" fill="#e8a33d" />
        <circle cx="0" cy="-24" r="14" fill="#f5d9b0" />
        <path d="M-14 -28 a14 12 0 0 1 28 0 z" fill="#6b4a26" />
      </g>
      <text x="7" y="16" fontSize="11" fontWeight="700" fill="#8a7a66">{n}</text>
      <text x="43" y="80" fontSize="8.5" textAnchor="middle" fill="#7a6a58">{label}</text>
    </g>
  );
  return (
    <svg viewBox="0 0 190 190" width="150" height="150" role="img" aria-label="2×2 격자 예시">
      <rect width="190" height="190" rx="8" fill="#ffffff" stroke="#c9b79c" strokeWidth="2" />
      {cell(6, 6, "1", 6, "다리 벌림")}
      {cell(98, 6, "2", 0, "모음")}
      {cell(6, 98, "3", -6, "반대로 벌림")}
      {cell(98, 98, "4", 0, "모음")}
    </svg>
  );
}

export function GridHelp() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 클립보드가 막힌 브라우저 — 아래 글상자에서 직접 긁어 가면 된다
      setCopied(false);
    }
  }

  return (
    <div className="ghelp">
      <button type="button" className="ghelp__toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} 2×2 격자가 뭔가요? — 처음이면 눌러 보세요
      </button>

      {open ? (
        <div className="ghelp__body">
          <div className="ghelp__row">
            <GridShape />
            <div>
              <b className="ghelp__h">이렇게 생긴 그림 한 장을 올립니다</b>
              <p className="ghelp__p">
                걷는 동작 네 장면을 <b>한 장에 2×2 로</b> 그린 그림이에요.
                네 장을 따로 받으면 <b>크기가 제각각</b>이 돼서 걸을 때 덜컹거립니다 —
                한 장에 그리면 AI 가 같은 크기로 그려 줘요.
              </p>
              <p className="ghelp__p ghelp__p--dim">
                올리면 자르기·배경 지우기·키 맞추기·발끝 맞추기까지 자동으로 합니다.
              </p>
            </div>
          </div>

          <b className="ghelp__h">① 어디서 만드나요</b>
          <p className="ghelp__p">
            <a href="https://gemini.google.com/" target="_blank" rel="noreferrer">gemini.google.com</a>
            {" "}(무료, 구글 계정만 있으면 됩니다). 또는 ChatGPT.
          </p>

          <b className="ghelp__h">② 캐릭터 그림을 먼저 첨부하세요</b>
          <p className="ghelp__p">
            <b>안 붙이면 다른 아이가 나옵니다.</b> 옆으로 걷는 걸 만들 땐 <b>옆모습</b> 그림을 붙이세요.
          </p>

          <b className="ghelp__h">③ 아래를 복사해 붙여넣기</b>
          <div className="ghelp__prompt">
            <button type="button" className="ghelp__copy" onClick={() => void copy()}>
              {copied ? "복사했어요" : "복사"}
            </button>
            <textarea className="ghelp__ta" readOnly rows={6} value={PROMPT} />
          </div>
          <p className="ghelp__p ghelp__p--dim">
            앞모습은 <code>SIDE view</code> 를 <code>FRONT view (facing the viewer)</code> 로,
            뒷모습은 <code>BACK view (seen from behind)</code> 로 바꾸세요.
          </p>

          <b className="ghelp__h">자주 있는 일</b>
          <ul className="ghelp__list">
            <li><b>다른 아이가 나와요</b> — 캐릭터 그림 첨부를 빠뜨린 거예요</li>
            <li><b>칸이 4개가 아니에요</b> — <code>exactly 4 cells in a 2x2 grid</code> 를 덧붙이세요</li>
            <li><b>배경에 바닥·그림자가 있어요</b> — <code>no shadow, no ground, no floor</code> 를 덧붙이세요</li>
            <li><b>크기가 조금씩 달라요</b> — 괜찮아요. 올릴 때 자동으로 맞춥니다</li>
            <li><b>선이 검게 나와요</b> — <code>thick soft brown outlines (not black)</code> 를 덧붙이세요</li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
